import { Helmet } from "react-helmet-async";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import { fadeUp } from "@/lib/motion";
import { getServiceBySlug, services } from "@/lib/services";

const SITE_URL = "https://psyloveyourself.com";

export default function Service() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  if (!service) return <Navigate to="/" replace />;

  const url = `${SITE_URL}/ipiresies/${service.slug}`;
  const others = services.filter((s) => s.slug !== service.slug).slice(0, 3);

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalTherapy",
    name: service.h1,
    description: service.metaDescription,
    inLanguage: "el-GR",
    url,
    provider: {
      "@type": "Person",
      name: "Άννα Παπαχρονόπουλου",
      jobTitle: "Ψυχολόγος",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Ρήγα Φεραίου 119",
        addressLocality: "Πάτρα",
        addressCountry: "GR",
      },
    },
    areaServed: { "@type": "City", name: "Πάτρα" },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Αρχική", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Υπηρεσίες", item: `${SITE_URL}/#services` },
      { "@type": "ListItem", position: 3, name: service.h1, item: url },
    ],
  };

  return (
    <>
      <Helmet>
        <html lang="el-GR" />
        <title>{service.metaTitle}</title>
        <meta name="description" content={service.metaDescription} />
        <meta name="keywords" content={service.keywords} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={service.metaTitle} />
        <meta property="og:description" content={service.metaDescription} />
        <meta property="og:url" content={url} />
        <meta property="og:locale" content="el_GR" />
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <Header />

      <main className="bg-background pt-32 pb-24" data-testid="service-page">
        <article className="max-w-[1100px] mx-auto px-6 sm:px-12 lg:px-20">
          <motion.div {...fadeUp}>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-body text-muted-foreground hover:text-foreground transition-colors mb-10"
            >
              <ArrowLeft size={14} strokeWidth={1.5} />
              Αρχική
            </Link>

            <p className="text-[11px] tracking-[0.25em] uppercase font-body font-medium text-primary mb-5">
              Υπηρεσία
            </p>
            <h1 className="font-heading font-medium tracking-tight text-foreground text-4xl sm:text-5xl lg:text-[64px] leading-[1.04]">
              {service.h1}
            </h1>
            <p className="mt-7 font-body font-light text-lg sm:text-xl text-foreground/75 leading-relaxed max-w-[760px]">
              {service.intro}
            </p>
          </motion.div>

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {service.sections.map((sec, idx) => (
              <motion.div
                key={sec.title}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: idx * 0.1 }}
                className="bg-card rounded-3xl p-8 sm:p-10 border border-border/50"
                data-testid={`service-section-${idx}`}
              >
                <h2 className="font-heading text-2xl sm:text-3xl text-foreground tracking-tight">
                  {sec.title}
                </h2>
                <ul className="mt-6 space-y-3.5">
                  {sec.items.map((it) => (
                    <li
                      key={it}
                      className="flex items-start gap-3 font-body font-light text-foreground/80 leading-relaxed"
                    >
                      <span className="mt-1 w-5 h-5 shrink-0 rounded-full bg-primary/15 flex items-center justify-center text-primary">
                        <Check size={12} strokeWidth={2} />
                      </span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Duration */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="mt-10 inline-flex items-center gap-3 bg-card rounded-full px-6 py-3.5 border border-border/60"
          >
            <span className="text-[10px] tracking-[0.22em] uppercase font-body font-medium text-muted-foreground">
              Διάρκεια
            </span>
            <span className="font-body text-foreground">{service.duration}</span>
          </motion.div>

          {/* CTA */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.25 }}
            className="mt-16 p-8 sm:p-12 bg-foreground text-background rounded-3xl"
          >
            <h3 className="font-heading text-3xl sm:text-4xl tracking-tight">
              Έτοιμος/η να ξεκινήσεις;
            </h3>
            <p className="mt-3 font-body font-light text-background/85 leading-relaxed max-w-[520px]">
              Η πρώτη συνεδρία είναι ένας χώρος γνωριμίας — χωρίς δέσμευση. Δια
              ζώσης στη Ρήγα Φεραίου 119, Πάτρα ή online.
            </p>
            <Link
              to="/#contact"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-background text-foreground px-7 py-3.5 font-body text-sm font-medium hover:-translate-y-0.5 transition-all duration-300"
              data-testid="service-cta"
            >
              Κλείσε συνεδρία
              <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </motion.div>

          {/* Other services */}
          <div className="mt-20 pt-12 border-t border-border/50">
            <p className="text-[11px] tracking-[0.25em] uppercase font-body font-medium text-primary mb-6">
              Άλλες υπηρεσίες
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {others.map((o) => (
                <Link
                  key={o.slug}
                  to={`/ipiresies/${o.slug}`}
                  className="group block bg-card border border-border/50 rounded-2xl p-6 hover:-translate-y-1 hover:bg-background transition-all duration-500"
                  data-testid={`other-service-${o.slug}`}
                >
                  <h4 className="font-heading text-xl text-foreground tracking-tight leading-tight">
                    {o.h1}
                  </h4>
                  <span className="mt-3 inline-flex items-center gap-2 text-sm font-body text-foreground/70 group-hover:text-foreground transition-colors">
                    Δες περισσότερα
                    <ArrowRight size={14} strokeWidth={1.5} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}
